import React, { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { colors, constants } from "utils";
import { useLandingPageView } from "hooks/usePageViewTracker";
import {
  PAGE_NAME,
  trackBackToGroups,
  trackGradeFilter,
  trackSetSelected,
  trackWarmUpSetOpened,
} from "./warmUpAnalytics";
import { AssignGroupsModal } from "./AssignGroupsModal";
import { ChevronLeftIcon } from "./icons";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { SetCard, SetCardSkeleton } from "./SetCard";
import { SetPreview } from "./SetPreview";
import { fetchFreeCodes, fetchWarmUpSets, isUnreachableClass } from "./warmUpApi";
import { ALL_GRADES, gradeOptions, questionSetCountLabel, setsForGrade } from "./warmUpSets";
import {
  Message,
  SecondaryButton,
  TextButton,
  VisuallyHidden,
  interText,
  marfaTitle,
} from "./styles";

const SUBTITLE =
  "Warm-Up Challenges need no prior instruction. They are the perfect way to get every student thinking and talking about math!";

// The two-line title handed to the app's Header (which is not edited).
export const WarmUpChallengesHeader = () => (
  <div>
    <div>Select a Warm-Up Challenge</div>
    <Subtitle>{SUBTITLE}</Subtitle>
  </div>
);

// Until the list loads the filter shows All Grades and stays shut.
const LOADING_GRADE_CHOICES = [{ value: ALL_GRADES, label: "All Grades" }];

// The class is read from the query once, on mount; later changes to the URL
// do not move the page to another class.
const useClassroomIdOnMount = () => {
  const { search } = useLocation();
  const [classroomId] = useState(() => new URLSearchParams(search).get("classroomId") || "");
  return classroomId;
};

export const WarmUpChallengesPage = () => {
  const classroomId = useClassroomIdOnMount();
  if (!classroomId) return <Redirect to={constants.EDUCATOR_PATH} />;
  return <WarmUpChallengesBody classroomId={classroomId} />;
};

const LIST_LOADING = "loading";
const LIST_FAILED = "failed";
const LIST_READY = "ready";
const SKELETON_CARDS = 6;
const PREVIEW_STEP = "preview";
const ASSIGN_STEP = "assign";

// GET /warm-up-sets once, without a grade; `retry` asks again after a failure.
const useWarmUpSets = () => {
  const [list, setList] = useState({ status: LIST_LOADING, sets: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setList({ status: LIST_LOADING, sets: [] });
    fetchWarmUpSets()
      .then(({ warmup_sets: listed }) => active && setList({ status: LIST_READY, sets: listed }))
      .catch(() => active && setList({ status: LIST_FAILED, sets: [] }));
    return () => {
      active = false;
    };
  }, [attempt]);

  return { ...list, retry: () => setAttempt((count) => count + 1) };
};

// Closing the preview or the modal puts focus back on the card the flow
// started from, after the dialog has unmounted.
const useCardFocusReturn = () => {
  const cards = useRef({});
  const [focusTarget, setFocusTarget] = useState(null);

  useEffect(() => {
    if (!focusTarget) return;
    cards.current[focusTarget.setId]?.focus();
  }, [focusTarget]);

  return {
    cardRef: (setId) => (element) => {
      cards.current[setId] = element;
    },
    returnFocusTo: (setId) => setFocusTarget({ setId }),
  };
};

export const manageGroupsUrl = (classroomId) => `${constants.EDUCATOR_PATH}?classId=${classroomId}`;

const WarmUpChallengesBody = ({ classroomId }) => {
  const history = useHistory();
  const { status, sets, retry } = useWarmUpSets();
  const [grade, setGrade] = useState(ALL_GRADES);
  const [classUnreachable, setClassUnreachable] = useState(false);
  const [flow, setFlow] = useState(null);
  const { cardRef, returnFocusTo } = useCardFocusReturn();
  useLandingPageView(PAGE_NAME, { class: classroomId });

  const openPreview = (set) => {
    trackWarmUpSetOpened(classroomId, set);
    setFlow({ step: PREVIEW_STEP, set });
  };

  const selectSet = (set) => {
    trackSetSelected(classroomId, set);
    setFlow({ step: ASSIGN_STEP, set });
  };

  const endFlow = () => {
    returnFocusTo(flow.set.id);
    setFlow(null);
  };

  const goBack = () => {
    trackBackToGroups(classroomId);
    history.push(manageGroupsUrl(classroomId));
  };

  // Re-picking the shown grade is no change, as with the native select before.
  const changeGrade = (value) => {
    if (value === grade) return;
    trackGradeFilter(classroomId, value);
    setGrade(value);
  };

  useEffect(() => {
    let active = true;
    fetchFreeCodes(classroomId).catch((error) => active && setClassUnreachable(isUnreachableClass(error)));
    return () => {
      active = false;
    };
  }, [classroomId]);

  if (classUnreachable) return <Redirect to={constants.EDUCATOR_PATH} />;

  const ready = status === LIST_READY;
  const shownSets = setsForGrade(sets, grade);
  const gradeChoices = ready ? gradeOptions(sets) : LOADING_GRADE_CHOICES;

  return (
    <PageContainer>
      <TopBlock>
        <TextButton type="button" onClick={goBack}>
          <ChevronLeftIcon stroke={colors.blue700} />
          <span>Back</span>
        </TextButton>
        <SectionTitle>Warm-Up Challenge Sets</SectionTitle>
      </TopBlock>
      <FilterRow>
        <SelectDropdown
          appearance="figma"
          size="small"
          label="Grades"
          value={grade}
          displayText={gradeChoices.find(({ value }) => value === grade)?.label}
          options={gradeChoices}
          onChange={changeGrade}
          disabled={!ready}
        />
      </FilterRow>
      {status === LIST_LOADING && <LoadingGrid />}
      {status === LIST_FAILED && <ListError onRetry={retry} />}
      {ready && <CountLine>{questionSetCountLabel(shownSets.length)}</CountLine>}
      {ready && sets.length === 0 && <Message>No warm-up sets are available yet.</Message>}
      {ready && sets.length > 0 && (
        <Grid>
          {shownSets.map((set) => (
            <Slot key={set.id}>
              <SetCard set={set} onOpen={openPreview} cardRef={cardRef(set.id)} />
            </Slot>
          ))}
        </Grid>
      )}
      {flow?.step === PREVIEW_STEP && <SetPreview set={flow.set} onClose={endFlow} onSelect={selectSet} />}
      {flow?.step === ASSIGN_STEP && (
        <AssignGroupsModal
          set={flow.set}
          classroomId={classroomId}
          onClose={endFlow}
          onSaved={() => history.push(manageGroupsUrl(classroomId))}
          onClassUnreachable={() => setClassUnreachable(true)}
        />
      )}
    </PageContainer>
  );
};

const LoadingGrid = () => (
  <>
    <VisuallyHidden role="status">Loading warm-up sets</VisuallyHidden>
    <Grid>
      {Array.from({ length: SKELETON_CARDS }, (_, index) => (
        <Slot key={index} aria-hidden="true">
          <SetCardSkeleton />
        </Slot>
      ))}
    </Grid>
  </>
);

const ListError = ({ onRetry }) => (
  <ErrorBlock>
    <Message role="alert">We couldn&apos;t load the warm-up sets.</Message>
    <SecondaryButton type="button" onClick={onRetry}>
      <span>Try Again</span>
    </SecondaryButton>
  </ErrorBlock>
);

const Subtitle = styled.p`
  ${interText(14, 20, 400, "0")}
  color: ${colors.milkGlass500};
  margin: 8px 0 0;
`;

const PageContainer = styled.div`
  background: ${colors.milkGlass500};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: calc(100vh - 120px);
  padding: 32px;
`;

const TopBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  ${marfaTitle}
`;

const FilterRow = styled.div`
  width: 160px;
`;

const ErrorBlock = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CountLine = styled.p`
  ${interText(18, 26, 700)}
  margin: 0;
`;

// As in Figma 6347:20009, a 423px column fits while it ends 17px or more
// before main content's right edge: the grid may run 15px into the page's
// 32px right padding (three columns at 1366 with the nav hidden).
const GRID_INTO_RIGHT_PADDING = 15;

const Grid = styled.ul`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fill, 423px);
  justify-content: start;
  list-style: none;
  margin: 0;
  margin-right: -${GRID_INTO_RIGHT_PADDING}px;
  padding: 0;
`;

const Slot = styled.li`
  height: 296px;
  position: relative;
  width: 423px;
`;
